import { $PageLevelTraffic } from 'shared/atoms';
import { buildSchema, DeepLinkBuildingProps } from './schema';
import { $ABTest, $UserAgent, $Traffic } from '@lemon8/webapp-shared/model';
import { $EnablePintrk } from '~/models/enable-pintrk';
import { $LibraOptimizeOnelinkActive } from '~/utils/libra';

export interface ActiveProps {
  pid?: string;
  schemaOption: DeepLinkBuildingProps;
  ampExtra: Record<string, string | null>;
}

const modifyURLForSEM = (onelink: URL): void => {
  const href = new URL(location.href);

  if (href.searchParams.get('ad_platform_id') !== 'googleadwords_int_lead') {
    return;
  }

  onelink.searchParams.set('pid', 'google_sem');
  onelink.searchParams.set('af_prt', 'wezonet');

  const afParameters = ['af_c_id', 'af_adset_id', 'af_ad_id', 'af_keywords', 'af_channel', 'placement', 'target'];

  afParameters.forEach((parameter) => {
    const value = href.searchParams.get(parameter);
    if (value) {
      onelink.searchParams.set(parameter, value);
    }
  });
};

const modifyURLForPinterest = (onelink: URL): void => {
  const href = new URL(location.href);

  const afParameters = [
    'pid',
    'af_siteid',
    'c',
    'af_adset',
    'af_adset_id',
    'af_ad',
    'af_ad_id',
    'af_c_id',
    'af_click_lookback',
    'clickid',
    'sha1_idfa',
    'tracking',
    'appid',
    'timestamp',
    'af_prt',
  ];

  afParameters.forEach((parameter) => {
    const value = href.searchParams.get(parameter);
    if (value) {
      onelink.searchParams.set(parameter, value);
    }
  });
};

export const generateOneLink = (props: ActiveProps): string => {
  const { schemaOption, ampExtra, pid: overridePid } = props;
  const store = window.store;
  const { seoPageId } = store.get($PageLevelTraffic);
  const { pid: trafficPid } = store.get($Traffic);
  const { versions } = store.get($ABTest);
  const { isiOS, isMacOS } = store.get($UserAgent);
  const enablePintrk = store.get($EnablePintrk);

  const pid = overridePid || trafficPid;

  const schema = buildSchema({
    ...schemaOption,
    pid,
  });

  const onelink = new URL('https://lemon8.onelink.me/FMQw');
  onelink.searchParams.set('pid', pid);
  onelink.searchParams.set('af_force_dp', 'false');
  onelink.searchParams.set('af_dp', schema);
  onelink.searchParams.set('retargeting', 'true');
  onelink.searchParams.set('ab_version', versions);
  onelink.searchParams.set(
    'af_web_dp',
    isiOS || isMacOS
      ? 'https://itunes.apple.com/app/apple-store/id1498607143?pt=1613620&ct=interstitialdownload&mt=8'
      : 'https://play.google.com/store/apps/details?id=com.bd.nproject',
  );
  onelink.searchParams.set(
    'amp_extra',
    JSON.stringify({
      seo_page_id: seoPageId,
      traffic_type: pid,
      ...ampExtra,
    }),
  );

  if (props.ampExtra.source_group_id) {
    onelink.searchParams.set('af_ad', `?${props.ampExtra.source_group_id}`);
  }

  modifyURLForSEM(onelink);
  if (enablePintrk) {
    modifyURLForPinterest(onelink);
  }

  return onelink.toString();
};

export const active = (props: ActiveProps): void => {
  const onelink = generateOneLink(props);

  const optimizeOnelinkActive = window.store.get($LibraOptimizeOnelinkActive);
  const enablePintrk = window.store.get($EnablePintrk);

  if (optimizeOnelinkActive) {
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (enablePintrk && 'pintrk' in window) {
          window.pintrk('track', 'lead', {
            event_id: 'eventId0001',
            lead_type: 'Newsletter',
          });
        }

        window.open(onelink);
      }, 0);
    });
  } else {
    if (enablePintrk && 'pintrk' in window) {
      window.pintrk('track', 'lead', {
        event_id: 'eventId0001',
        lead_type: 'Newsletter',
      });
    }

    window.open(onelink);
  }
};
